/* Shared helpers used across the D2C Allocation tabs. */
import { TAXONOMY, classifyMargin, deriveServiceTypesFromGroups } from './constants';

// ── Map builders — convert flat API arrays into O(1) lookup maps ─────────────

// From GET /api/allocation/supply-costs → zone→weight→payment→ac→cost
export function buildCostMap(rows = []) {
  const map = {};
  for (const r of rows) {
    if (!r.zone || !r.ac) continue;
    if (!map[r.zone]) map[r.zone] = {};
    if (!map[r.zone][r.weight]) map[r.zone][r.weight] = {};
    if (!map[r.zone][r.weight][r.payment]) map[r.zone][r.weight][r.payment] = {};
    map[r.zone][r.weight][r.payment][r.ac] = r.cost;
  }
  return map;
}

// From GET /api/allocation/supply-costs (RVP rows) → zone→weight→qcMode→ac→cost
// Requires backend to include `service` and `qcMode` on SupplyCostDto — see D2C_ALLOCATION_BACKEND_CHANGES.md
export function buildRvpCostMap(rows = []) {
  const map = {};
  for (const r of rows) {
    if (r.service !== 'RVP' || !r.qcMode || !r.ac) continue;
    if (!map[r.zone]) map[r.zone] = {};
    if (!map[r.zone][r.weight]) map[r.zone][r.weight] = {};
    if (!map[r.zone][r.weight][r.qcMode]) map[r.zone][r.weight][r.qcMode] = {};
    map[r.zone][r.weight][r.qcMode][r.ac] = r.cost;
  }
  return map;
}

// From GET /api/allocation/revenue/{merchantId} → zone→weight→payment→parent→revenue
export function buildRevenueMap(rows = []) {
  const map = {};
  for (const r of rows) {
    if (!r.zone || !r.parent) continue;
    if (!map[r.zone]) map[r.zone] = {};
    if (!map[r.zone][r.weight]) map[r.zone][r.weight] = {};
    if (!map[r.zone][r.weight][r.payment]) map[r.zone][r.weight][r.payment] = {};
    map[r.zone][r.weight][r.payment][r.parent] = r.revenue;
  }
  return map;
}

// A reverse account code is available for allocation unless it has an EXPLICIT
// acEnabled entry that turns both prepaid and cod off. No entry → globally
// available (treated as enabled). This MUST match the Reverse Allocation tab's
// selection rule so the preview UI, coverage analytics, and downloaded CSV show
// exactly the reverse couriers the user was allowed to allocate — otherwise the
// tab lets you pick couriers that then silently vanish from the preview/Excel.
export function isReverseAcAvailable(acEnabled, ac) {
  const en = acEnabled?.[ac];
  return !en || en.prepaid === 1 || en.cod === 1;
}

// Normalize priority type — API returns 'COST'/'MARGIN'/'SPEED'; UI uses 'cost'/'margin'/'custom'.
export function normPriorityType(pt) {
  const u = String(pt || '').toUpperCase();
  if (u === 'COST' || u === 'CHEAPEST') return 'cost';
  if (u === 'SPEED' || u === 'CUSTOM') return 'custom';
  return 'margin';
}

// Explicit priority order from a custom rule (UI: customOrder, API: couriers).
export function ruleCouriers(rule) {
  return rule?.customOrder?.length ? rule.customOrder : (rule?.couriers || []);
}

// Re-order a custom rule's couriers into the SAME order the Priority-order panel
// displays them: grouped by service type (rule.serviceTypes order first, then any
// leftovers), preserving customOrder within each group. The UI groups + renumbers
// the list this way, so publish/preview MUST use this order — otherwise the P1/P2/P3
// the user sees won't match what gets published (the raw customOrder array). Never
// drops a courier.
export function orderCustomCouriersByDisplay(rule, acServiceTypes = {}) {
  const order = ruleCouriers(rule);
  if (order.length <= 1) return order;
  const sts = rule?.serviceTypes || [];
  const primarySt = (ac) => {
    const acSts = acServiceTypes[ac] || [];
    return sts.find((st) => acSts.includes(st)) || acSts[0] || '__none__';
  };
  const groups = new Map();
  for (const ac of order) {
    const k = primarySt(ac);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(ac);
  }
  const keys = [];
  const seen = new Set();
  for (const st of sts) if (groups.has(st) && !seen.has(st)) { keys.push(st); seen.add(st); }
  for (const k of groups.keys()) if (!seen.has(k)) { keys.push(k); seen.add(k); }
  return keys.flatMap((k) => groups.get(k));
}

// Resolve the ordered reverse-courier priority for one lane (zone × weight × qcMode).
// SINGLE SOURCE OF TRUTH for reverse priority — used by the CSV/publish builder AND
// the Preview & Publish reverse table so they always agree:
//   • a matching enabled custom rule → its explicit courier order
//   • otherwise → all available reverse ACs (default cascade)
// Returns { rule, ordered } where `ordered` is the priority-ranked AC list.
export function resolveReverseLanePriority({ reverseRules = [], reverseAcs = [], zone, weight, qcMode, acServiceTypes = {} }) {
  // RVP lanes are emitted per QC mode, so a rule applies to a QC-mode row only when its
  // QC Mode condition includes that mode (empty qcModes = applies to both). This lets a
  // With-QC rule and a Non-QC rule both produce entries on the same zone × weight lane.
  const rule = (reverseRules || []).find((r) => {
    if (r.disabled || r._dead) return false;
    if (r.zones?.length && !r.zones.includes(zone)) return false;
    if (r.weightFrom != null && weight < Number(r.weightFrom)) return false;
    if (r.weightTo != null && weight > Number(r.weightTo)) return false;
    if (qcMode && r.qcModes?.length && !r.qcModes.some((m) => (m === 'WITHQC' ? 'QC' : m) === qcMode)) return false;
    return true;
  }) || null;
  const anyEnabledRule = (reverseRules || []).some((r) => r && !r.disabled && !r._dead);
  let ordered;
  if (rule) {
    // Custom rule → couriers in the SAME order the Priority-order panel shows (so the
    // published P1/P2/P3 match the numbers the user arranged). Non-custom → default cascade.
    ordered = (normPriorityType(rule.priorityType) === 'custom' && ruleCouriers(rule).length)
      ? orderCustomCouriersByDisplay(rule, acServiceTypes)
      : reverseAcs;
  } else {
    // No rule covers this lane. If the merchant has ANY reverse rule, the lane is
    // intentionally uncovered → emit nothing (option A: a rule's weight range IS its
    // range, no default padding beyond it — matches forward custom). With no reverse
    // rules at all, fall back to the default cascade so reverse isn't empty.
    ordered = anyEnabledRule ? [] : reverseAcs;
  }
  return { rule, ordered };
}

// From GET /api/order/serviceability/intel/setup/groups
// Groups shape: [{ code: ST_ID, linkedCodes: [{ cpAccountCode, parent, isActive, direction }] }]
// Returns: { [ac]: { ac, parent, direction, mode, prepaid, cod } }
// Note: `mode`, `prepaid`, `cod` default until GET /api/allocation/ac/meta includes those fields.
export function buildAcMetaFromGroups(groups = []) {
  const map = {};
  for (const g of groups) {
    for (const lc of (g.linkedCodes || [])) {
      if (lc.isActive === false) continue;
      const ac = lc.cpAccountCode;
      if (!ac) continue;
      // Use lc.direction (linked-code level) — the API sets it correctly per AC
      // e.g. Xpressbees_Reverse_BeatXP has direction RVP even inside FWD_SURFACE_REG
      const lcDir = String(lc.direction || '').toUpperCase();
      const direction = (lcDir === 'RVP' || lcDir === 'REVERSE') ? 'Reverse' : 'Forward';
      if (!map[ac]) {
        map[ac] = {
          ac,
          parent: lc.parent || ac,
          direction,
          mode: 'SURFACE',
          prepaid: 1,
          cod: 1,
        };
      } else if (direction === 'Forward' && map[ac].direction === 'Reverse') {
        // AC appears in multiple groups; if any group has it as Forward, mark Forward
        map[ac].direction = 'Forward';
      }
    }
  }
  return map;
}

// Returns: { [ac]: [ST_ID, ...] }
export function buildAcServiceTypesFromGroups(groups = []) {
  const map = {};
  for (const g of groups) {
    const stId = g.code;
    if (!stId) continue;
    for (const lc of (g.linkedCodes || [])) {
      if (lc.isActive === false) continue;
      const ac = lc.cpAccountCode;
      if (!ac) continue;
      if (!map[ac]) map[ac] = [];
      if (!map[ac].includes(stId)) map[ac].push(stId);
    }
  }
  return map;
}

// ── Core computations ─────────────────────────────────────────────────────────

export function computeMarginPct({ zone, weight, payment, ac, acMeta = {}, costMap = {}, revenueMap = {} }) {
  const meta = acMeta[ac];
  if (!meta) return null;
  const cost = costMap?.[zone]?.[weight]?.[payment]?.[ac];
  const revenue = revenueMap?.[zone]?.[weight]?.[payment]?.[meta.parent];
  if (cost == null || revenue == null || revenue === 0) return null;
  return ((revenue - cost) / revenue) * 100;
}

export function classifyAcMargin(args) {
  return classifyMargin(computeMarginPct(args));
}

// Return an ordered courier list for a single lane.
// acMeta, acServiceTypes, costMap, revenueMap are built from real APIs via the map builders above.
export function rankAcsForLane({
  zone,
  weight,
  payment,
  serviceTypes = [],
  mode = 'margin',
  acEnabled,
  groupSameParent = true,
  acMeta = {},
  acServiceTypes = {},
  costMap = {},
  revenueMap = {},
}) {
  if (!serviceTypes.length || !Object.keys(acMeta).length) return [];

  const candidates = [];
  for (const ac of Object.keys(acMeta)) {
    const meta = acMeta[ac];
    if (meta.direction !== 'Forward') continue;

    const stForAc = acServiceTypes[ac] || [];
    const matchingSts = stForAc.filter((st) => serviceTypes.includes(st));
    if (!matchingSts.length) continue;

    // Strict AC-master gate: AC must be explicitly enabled in the Account Codes tab.
    // ACs with no entry (never configured) are treated as disabled.
    const en = acEnabled?.[ac];
    if (!en || (en.prepaid !== 1 && en.cod !== 1)) continue;

    // Payment-specific gate: PREPAID lanes need prepaid=1; COD lanes need cod=1.
    if (payment === 'PREPAID' && en.prepaid !== 1) continue;
    if (payment === 'COD'     && en.cod     !== 1) continue;

    // Supply cost may be absent (e.g. margin is computed backend-side and supply
    // costs aren't uploaded locally). Don't drop the courier — keep it so the lane
    // still lists every eligible enabled AC; priced ACs simply rank ahead of unpriced.
    const cost = costMap?.[zone]?.[weight]?.[payment]?.[ac] ?? null;

    const revenue = revenueMap?.[zone]?.[weight]?.[payment]?.[meta.parent];
    const marginPct = (cost != null && revenue != null && revenue > 0)
      ? ((revenue - cost) / revenue) * 100
      : null;

    candidates.push({
      ac,
      parent: meta.parent,
      mode: meta.mode || 'SURFACE',
      cost,
      revenue: revenue ?? null,
      marginPct,
      band: classifyMargin(marginPct),
      serviceTypes: matchingSts,
      // Cascade priority follows the ORDER service types were selected (their index
      // in `serviceTypes`), not the predefined speed rank — first-selected = highest.
      rank: Math.min(...matchingSts.map((st) => serviceTypes.indexOf(st))),
    });
  }

  // Unpriced ACs (cost null) sort after priced ones within the same service-type rank.
  const costOf = (x) => (x.cost == null ? Infinity : x.cost);
  candidates.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (mode === 'cheapest') return costOf(a) - costOf(b);
    // margin mode: ACs without computable margin go to the end
    if (a.marginPct == null && b.marginPct == null) return 0;
    if (a.marginPct == null) return 1;
    if (b.marginPct == null) return -1;
    return b.marginPct - a.marginPct;
  });

  if (!groupSameParent) return candidates;

  const seen = new Set();
  const out = [];
  for (const c of candidates) {
    if (seen.has(c.parent)) continue;
    seen.add(c.parent);
    const siblings = candidates.filter((x) => x.parent === c.parent);
    siblings.sort((a, b) => costOf(a) - costOf(b));
    out.push(...siblings);
  }
  return out;
}

// {bandId: count} summary across all weight bands for one zone+payment.
export function buildMarginSummary({
  zone, payment, autoServiceTypes, acEnabled, groupSameParent,
  acMeta = {}, acServiceTypes = {}, costMap = {}, revenueMap = {},
}) {
  const counts = { prof: 0, bprof: 0, bloss: 0, loss: 0, dloss: 0, unk: 0 };
  for (const w of TAXONOMY.weightBands) {
    const ranked = rankAcsForLane({
      zone, weight: w, payment,
      serviceTypes: autoServiceTypes,
      acEnabled, groupSameParent,
      acMeta, acServiceTypes, costMap, revenueMap,
    });
    const p1 = ranked[0];
    if (!p1 || !p1.band) counts.unk += 1;
    else counts[p1.band.id] += 1;
  }
  return counts;
}

// Lanes where P1 is loss-making — used for the local publish gate preview.
export function detectLossMakingLanes({
  autoServiceTypes, acEnabled, groupSameParent, forwardMode = 'margin', groups = [],
  acMeta = {}, acServiceTypes = {}, costMap = {}, revenueMap = {},
}) {
  const losses = [];
  const fromGroups = deriveServiceTypesFromGroups(groups).filter((s) => s.dir === 'Forward').map((s) => s.id);
  const sts = autoServiceTypes?.length ? autoServiceTypes : fromGroups;

  for (const zone of TAXONOMY.zones.map((z) => z.id)) {
    for (const weight of TAXONOMY.weightBands) {
      for (const payment of ['PREPAID', 'COD']) {
        const ranked = rankAcsForLane({
          zone, weight, payment, serviceTypes: sts,
          mode: forwardMode, acEnabled, groupSameParent,
          acMeta, acServiceTypes, costMap, revenueMap,
        });
        const p1 = ranked[0];
        if (!p1?.band) continue;
        if (p1.band.id === 'loss' || p1.band.id === 'dloss') {
          losses.push({ zone, weight, payment, ac: p1.ac, bandId: p1.band.id, band: p1.band });
        }
      }
    }
  }
  return losses;
}

// Convert Redux acEnabled { ac: { prepaid, cod, zones? } } → the HIGHEST_PROZO_MARGIN
// publish shape. Opt-in model: an AC that is fully disabled (prepaid 0 AND cod 0) is
// omitted entirely, which excludes it from all rules. `zones` is an opt-OUT map —
// only the zones explicitly turned off (value 0) are emitted; an absent zones key
// means "included everywhere".
export function toApiAcEnabled(acEnabled = {}) {
  const out = {};
  for (const [ac, flags] of Object.entries(acEnabled)) {
    const prepaid = flags?.prepaid ?? 0;
    const cod = flags?.cod ?? 0;
    if (prepaid !== 1 && cod !== 1) continue; // fully disabled → exclude
    const entry = { prepaid, cod };
    const excludedZones = Object.entries(flags?.zones || {})
      .filter(([, v]) => v === 0)
      .reduce((acc, [z]) => ({ ...acc, [z]: 0 }), {});
    if (Object.keys(excludedZones).length) entry.zones = excludedZones;
    out[ac] = entry;
  }
  return out;
}

// Map the FE's internal custom-rule shape → the CUSTOM publish DTO. The UI stores the
// resolved courier order in `customOrder` and a margin/cost/custom priority token; the
// API DTO expects `couriers` + `priorityType: "RANKED"` (the backend re-derives the
// internal representation while parsing the consistent csvBlob). Only active rules are
// emitted; dead/disabled rules are dropped.
export function toApiCustomRules(rules = []) {
  // Weights come from text inputs and may be strings — the API expects numbers.
  const num = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return (rules || [])
    .filter((r) => r && !r.disabled && !r._dead)
    .map((r) => ({
      id: r.id,
      zones: [...(r.zones || [])],
      weightFrom: num(r.weightFrom),
      weightTo: num(r.weightTo),
      payment: [...(r.payment || [])],
      serviceTypes: [...(r.serviceTypes || [])],
      priorityType: 'RANKED',
      couriers: [...(r.customOrder?.length ? r.customOrder : r.couriers || [])],
    }));
}

// Map the FE's internal reverse-rule shape → the backend ReverseRuleDto:
// { id, zones, weightFrom, weightTo, qcModes, couriers }. (No payment/priorityType —
// the backend reverse DTO doesn't have those.)
export function toApiReverseRules(rules = []) {
  const num = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return (rules || [])
    .filter((r) => r && !r.disabled && !r._dead)
    .map((r) => ({
      id: r.id,
      zones: [...(r.zones || [])],
      weightFrom: num(r.weightFrom),
      weightTo: num(r.weightTo),
      qcModes: [...(r.qcModes || [])],
      couriers: [...(r.customOrder?.length ? r.customOrder : r.couriers || [])],
    }));
}

export function formatINR(value) {
  if (value == null) return '—';
  if (Number.isFinite(value)) return '₹' + Math.round(value).toLocaleString('en-IN');
  return value;
}

export function parsePincode(raw) {
  const s = String(raw || '').trim();
  if (/^\d{6}$/.test(s)) return s;
  return null;
}

// ── 39-column allocation CSV ──────────────────────────────────────────────────

export function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// A custom/reverse rule's optional-condition values, keyed for the 39-col CSV +
// the preview table. condValues is keyed by UI display label; '' when unset.
export function ruleConditionValues(rule) {
  const cv = (rule && rule.condValues) || {};
  const g = (k) => (cv[k] == null ? '' : cv[k]);
  return {
    minInvoice: g('Min Invoice'),
    maxInvoice: g('Max Invoice'),
    pickupPincode: g('Pickup Pincode'),
    dropPincode: g('Drop Pincode'),
    dispatchMode: g('Dispatch Mode'),
    shipmentType: g('Shipment Type'),
    itemUnits: g('Item Units'),
    uniqueSku: g('Unique SKU Count'),
    orderTime: g('Order Creation Time'),
    channel: g('Channel Name'),
  };
}

export function buildAllocationCsvRows({
  autoServiceTypes, acEnabled, groupSameParent, forwardMode = 'margin', groups = [],
  acMeta = {}, acServiceTypes = {}, costMap = {}, revenueMap = {},
  rvpEnabled = false, customRules = [], reverseRules = [], marginLanePriority = null,
  includeLossMaking = true,
}) {
  const headers = [
    ...TAXONOMY.csvSchema.fixedColumns,
    ...Array.from({ length: TAXONOMY.csvSchema.priorityColumns }, (_, i) => `Priority ${i + 1}`),
    ...TAXONOMY.csvSchema.trailingColumns,
  ];
  const rows = [headers];

  const fromGroups = deriveServiceTypesFromGroups(groups).filter((s) => s.dir === 'Forward').map((s) => s.id);
  const sts = autoServiceTypes?.length ? autoServiceTypes : fromGroups;

  // Normalize priority type — API returns 'COST'/'MARGIN'/'SPEED'; UI uses 'cost'/'margin'/'custom'.
  const normPt = (pt) => {
    const u = String(pt || '').toUpperCase();
    if (u === 'COST' || u === 'CHEAPEST') return 'cost';
    if (u === 'SPEED' || u === 'CUSTOM') return 'custom';
    return 'margin';
  };
  // Explicit priority order from a custom rule (UI: customOrder, API: couriers).
  const getCouriers = (rule) => (rule.customOrder?.length ? rule.customOrder : (rule.couriers || []));
  const rankByMode = (zone, weight, payment, mode, dropLoss = false) =>
    rankAcsForLane({
      zone, weight, payment, serviceTypes: sts, mode,
      acEnabled, groupSameParent, acMeta, acServiceTypes, costMap, revenueMap,
    })
      // dropLoss (margin rule, toggle off): drop negative-margin couriers per lane.
      // Couriers with no computable margin are kept (can't classify; backend keeps them too).
      .filter((c) => !dropLoss || c.marginPct == null || c.marginPct > 0)
      .map((c) => c.ac);

  // Map a rule's optional-condition values onto the fixed CSV columns (shared
  // with the Preview tab so the on-screen preview matches the published CSV).
  const condCols = ruleConditionValues;

  // The custom rule that governs a forward lane (zone × weight × payment), if any.
  const matchFwdRule = (zone, weight, payment) =>
    (customRules || []).find((r) => {
      if (r.disabled || r._dead) return false;
      if (r.zones?.length && !r.zones.includes(zone)) return false;
      if (r.payment?.length && !r.payment.includes(payment)) return false;
      if (r.weightFrom != null && weight < Number(r.weightFrom)) return false;
      if (r.weightTo != null && weight > Number(r.weightTo)) return false;
      return true;
    }) || null;

  // Resolve the priority AC list for one lane, honouring custom rules. Mirrors the
  // Preview & Publish tab's resolveFwdPriorities so the published CSV matches the
  // on-screen preview (fixes the stale-blob bug where custom order was ignored).
  const resolveFwdPriorities = (zone, weight, payment) => {
    if (forwardMode === 'custom') {
      const rule = matchFwdRule(zone, weight, payment);
      if (!rule) return [];
      const pt = normPt(rule.priorityType);
      if (pt === 'custom') return getCouriers(rule);
      // Margin rule with the loss-making toggle off → drop negative-margin couriers.
      const dropLoss = pt === 'margin' && !rule.includeLossMaking;
      return rankByMode(zone, weight, payment, pt === 'cost' ? 'cheapest' : 'margin', dropLoss);
    }
    return rankByMode(zone, weight, payment, forwardMode === 'cheapest' ? 'cheapest' : 'margin');
  };

  // Forward lanes.
  if (forwardMode === 'margin' && Array.isArray(marginLanePriority) && marginLanePriority.length) {
    // Highest Prozo Margin: the backend ranks each lane by margin (margin-preview's
    // lanePriority). Use that authoritative order so the CSV matches the on-screen
    // lane view exactly — only keeping couriers still enabled for that payment.
    const enabledForPayment = (ac, payment) => {
      const en = acEnabled?.[ac];
      if (!en) return false;
      return payment === 'COD' ? en.cod === 1 : en.prepaid === 1;
    };
    for (const lane of marginLanePriority) {
      const payment = String(lane.payment || '').toUpperCase();
      const ordered = (lane.couriers || [])
        .slice()
        // When loss-making couriers are excluded (toggle off, the default), drop the
        // negative-margin (red) couriers per lane so the CSV matches what the backend
        // publishes. includeLossMaking=true keeps every courier on the lane.
        .filter((c) => includeLossMaking || c.profitable)
        .sort((a, b) => (a.rank || 0) - (b.rank || 0))
        .map((c) => c.acCode)
        .filter((ac) => enabledForPayment(ac, payment));
      if (!ordered.length) continue;
      const priorities = Array.from({ length: 20 }, (_, i) => ordered[i] || '');
      rows.push([
        'FORWARD', lane.zone, lane.minWtKg, lane.maxWtKg,
        '', '', '', '',
        payment, '', '', '',
        '', '', '', '', '', '',
        ...priorities, '',
      ]);
    }
  } else if (forwardMode === 'custom') {
    // Custom mode: emit each rule as its OWN slab — one row per rule × zone × payment
    // using the rule's actual weight range (e.g. 0–10, 10–30) and its couriers/conditions.
    // (Previously rules were forced onto the fixed half-kg grid, which dropped any range
    // the grid didn't line up with — e.g. a 10–30 kg rule disappeared.)
    const allZones = TAXONOMY.zones.map((z) => z.id);
    const maxBand = TAXONOMY.weightBands[TAXONOMY.weightBands.length - 1];
    const pushRow = (zone, minW, maxW, payment, acList, c) => {
      const priorities = Array.from({ length: 20 }, (_, i) => acList[i] || '');
      rows.push([
        'FORWARD', zone, minW, maxW,
        c.minInvoice, c.maxInvoice, c.pickupPincode, c.dropPincode,
        payment, c.dispatchMode, c.shipmentType, '',
        c.itemUnits, c.uniqueSku, c.orderTime, '', '', '',
        ...priorities, c.channel,
      ]);
    };
    // Highest priority rule first (array order) so overlapping slabs read top-down.
    for (const rule of (customRules || [])) {
      if (!rule || rule.disabled || rule._dead) continue;
      const pt = normPt(rule.priorityType);
      const ruleZones = rule.zones?.length ? rule.zones : allZones;
      const rulePayments = (rule.payment?.length ? rule.payment : ['PREPAID', 'COD']).map((p) => String(p).toUpperCase());
      const wFrom = (rule.weightFrom != null && rule.weightFrom !== '') ? Number(rule.weightFrom) : 0;
      const wTo = (rule.weightTo != null && rule.weightTo !== '') ? Number(rule.weightTo) : maxBand;
      const c = condCols(rule);

      // Highest Prozo Margin priority → use the backend lanePriority (true margin order),
      // filtered to this rule's lanes, exactly like the rule editor. rankByMode sorts by
      // service-type cascade and would float loss-makers to P1, which is wrong here.
      if (pt === 'margin' && Array.isArray(marginLanePriority) && marginLanePriority.length) {
        const zoneSet = new Set(ruleZones);
        const paySet = new Set(rulePayments);
        for (const lane of marginLanePriority) {
          const payment = String(lane.payment || '').toUpperCase();
          if (zoneSet.size && !zoneSet.has(lane.zone)) continue;
          if (!paySet.has(payment)) continue;
          if (!(lane.minWtKg < wTo && lane.maxWtKg > wFrom)) continue; // weight overlap
          const acList = (lane.couriers || [])
            .slice()
            // Loss-making toggle off (default) → drop the red, negative-margin couriers
            // per lane, matching the rule editor's margin preview.
            .filter((cc) => rule.includeLossMaking || cc.profitable)
            .sort((a, b) => (a.rank || 0) - (b.rank || 0))
            .map((cc) => cc.acCode)
            .filter((ac) => {
              const en = acEnabled?.[ac];
              if (!en) return false;
              return payment === 'COD' ? en.cod === 1 : en.prepaid === 1;
            });
          if (acList.length) pushRow(lane.zone, lane.minWtKg, lane.maxWtKg, payment, acList, c);
        }
        continue;
      }

      for (const zone of ruleZones) {
        for (const payment of rulePayments) {
          if (pt === 'custom') {
            // Explicit courier order → a single row spanning the rule's full slab.
            const acList = getCouriers(rule);
            if (acList.length) pushRow(zone, wFrom, wTo, payment, acList, c);
          } else {
            // cost (or margin without lanePriority): rank per standard band inside the
            // rule's range so each sub-slab gets the right priority order.
            let prev = wFrom;
            for (const w of TAXONOMY.weightBands) {
              if (w <= wFrom || w > wTo) continue;
              const acList = rankByMode(zone, w, payment, pt === 'cost' ? 'cheapest' : 'margin', pt === 'margin' && !rule.includeLossMaking);
              if (acList.length) pushRow(zone, prev, w, payment, acList, c);
              prev = w;
            }
          }
        }
      }
    }
  } else {
    for (const zone of TAXONOMY.zones.map((z) => z.id)) {
      let prevWeight = 0;
      for (const w of TAXONOMY.weightBands) {
        for (const payment of ['PREPAID', 'COD']) {
          const acList = resolveFwdPriorities(zone, w, payment);
          if (!acList.length) continue;
          const priorities = Array.from({ length: 20 }, (_, i) => acList[i] || '');
          rows.push([
            'FORWARD', zone, prevWeight, w,
            '', '', '', '',
            payment, '', '', '',
            '', '', '', '', '', '',
            ...priorities, '',
          ]);
        }
        prevWeight = w;
      }
    }
  }

  // Reverse lanes — only when RVP is explicitly enabled by the user.
  const reverseAcs = rvpEnabled
    ? Object.keys(acMeta).filter((ac) =>
        acMeta[ac].direction === 'Reverse' && isReverseAcAvailable(acEnabled, ac))
    : [];
  if (reverseAcs.length > 0) {
    for (const zone of TAXONOMY.zones.map((z) => z.id)) {
      let prevWeight = 0;
      for (const w of TAXONOMY.weightBands) {
        // Emit one row per rule that covers this lane. QC Mode is courier-filtering
        // only — it does NOT decide whether a rule produces a row (so two rules on the
        // same zone×weight each get their own row regardless of QC mode overlap).
        const laneRules = reverseRules.filter((r) => {
          if (r.disabled || r._dead) return false;
          if (r.zones?.length && !r.zones.includes(zone)) return false;
          if (r.weightFrom != null && w < Number(r.weightFrom)) return false;
          if (r.weightTo != null && w > Number(r.weightTo)) return false;
          return true;
        });
        const anyEnabledRule = reverseRules.some((r) => r && !r.disabled && !r._dead);
        // laneRules empty + global rules exist → lane intentionally uncovered, skip.
        // laneRules empty + no rules at all → default cascade (emit one row with null rule).
        const emitRules = laneRules.length ? laneRules : (anyEnabledRule ? [] : [null]);
        const seenReverseRules = new Set();
        for (const rule of emitRules) {
          if (rule != null && seenReverseRules.has(rule.id)) continue;
          if (rule != null) seenReverseRules.add(rule.id);
          const { ordered } = resolveReverseLanePriority({
            reverseRules: rule != null ? [rule] : [],
            reverseAcs, zone, weight: w, acServiceTypes,
          });
          const candidates = ordered.slice(0, 20);
          if (!candidates.length) continue;
          const c = condCols(rule);
          const qcType = rule != null && (rule.activeConds || []).includes('QC Type') ? 'doorstep' : '';
          rows.push([
            'RVP', zone, prevWeight, w,
            c.minInvoice, c.maxInvoice, c.pickupPincode, c.dropPincode,
            'PREPAID', c.dispatchMode, c.shipmentType, '',
            c.itemUnits, c.uniqueSku, c.orderTime,
            qcType, '', '',
            ...Array.from({ length: 20 }, (_, i) => candidates[i] || ''), c.channel,
          ]);
        }
        prevWeight = w;
      }
    }
  }

  return rows;
}

export function rowsToCsv(rows) {
  return rows.map((r) => r.map(csvEscape).join(',')).join('\n');
}

export function ruleFingerprint(rule) {
  const parts = [
    [...(rule.zones || [])].sort().join(','),
    rule.weightFrom ?? '',
    rule.weightTo ?? '',
    [...(rule.payment || [])].sort().join(','),
    [...(rule.qcModes || [])].sort().join(','),
    [...(rule.serviceTypes || [])].sort().join(','),
    rule.priorityType || '',
    [...(rule.customOrder || [])].join('>'),
    rule.load || '',
  ];
  return parts.join('|');
}
